/**
 * The page's end of the account's hub (worker/hub.ts; the frames are
 * wire.ts's, "the account's hub"). One socket per page.
 *
 * It tells the hub which chat is in front, whether the page is visible and
 * focused, and when the person last used it — at most every 30 s, and at
 * once after a quiet spell. That is all the server knows of whether someone
 * is at a PC: a phone notification goes only when nobody is. In return the
 * hub sends what changed in the account's chats, so the list on every device
 * stays current and a reply started elsewhere is joined as it runs.
 *
 * A dropped socket comes back, more slowly each time; one the hub closed
 * with a `HUB_CLOSE` code does not.
 *
 * Assist's own window on a PC names it (`?device=`, docs/dispatch.md) once
 * it has published itself: the PC is online while that socket is, and the
 * hub sends it `wake`, `cancel`, `pair` and `approval`. No other page passes one.
 */

import { HUB_CLOSE, type HubClientFrame, type HubServerFrame } from '@wire';

/** How often the page says it is still there. */
export const PING_MS = 30_000;
/** The most often input is reported. */
export const ACTIVE_EVERY_MS = 30_000;
/** Seconds to wait before each try to reconnect; the last repeats. */
export const BACKOFF_S = [1, 2, 5, 15, 30, 60];

/** The wait before reconnect try `n` (from 0), in ms. */
export const backoffMs = (n: number): number => BACKOFF_S[Math.min(n, BACKOFF_S.length - 1)]! * 1000;

/** A close the page should not answer by reconnecting. */
export const isFinal = (code: number): boolean => (Object.values(HUB_CLOSE) as number[]).includes(code);

/** Is input at `now` worth reporting, the last report having gone at `last`? */
export const activeDue = (last: number, now: number): boolean => now - last >= ACTIVE_EVERY_MS;

/** The hub's address, with the PC named when this page is one. */
export const hubPath = (device?: string): string => `/api/assist/hub${device ? `?device=${encodeURIComponent(device)}` : ''}`;

/** The exact text the runtime answers without waking the hub. */
const PING = '{"t":"ping"}';
const INPUT = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

let front: string | null = null;
let tellState: () => void = () => {};

/** The chat in front of this page, or null for none. */
export function setFront(chatId: string | null): void {
  front = chatId;
  tellState();
}

/**
 * Open the hub and keep it open until the returned function is called.
 * `onFrame` has each frame; `onReconnect` runs after a reconnect, when what
 * was missed meanwhile must be read again. `device` is the PC this page is.
 */
export function startHub(onFrame: (frame: HubServerFrame) => void, onReconnect: () => void, device?: string): () => void {
  let socket: WebSocket | null = null;
  let stopped = false;
  let tries = 0;
  let opened = false;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let ping: ReturnType<typeof setInterval> | null = null;
  let lastActive = -Infinity;
  let lastState = '';

  const send = (frame: HubClientFrame) => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(frame));
  };
  const state = () => {
    const frame: HubClientFrame = {
      t: 'state',
      chatId: front,
      visible: document.visibilityState === 'visible',
      focused: document.hasFocus()
    };
    const text = JSON.stringify(frame);
    if (text === lastState || socket?.readyState !== WebSocket.OPEN) return;
    lastState = text;
    socket.send(text);
  };
  const input = () => {
    const now = Date.now();
    if (!activeDue(lastActive, now)) return;
    lastActive = now;
    send({ t: 'active' });
  };

  const connect = () => {
    const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${hubPath(device)}`);
    socket = ws;
    ws.onopen = () => {
      if (opened) onReconnect();
      opened = true;
      tries = 0;
      lastState = '';
      lastActive = -Infinity;
      state();
      ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(PING), PING_MS);
    };
    ws.onmessage = (e) => {
      let frame: HubServerFrame;
      try {
        frame = JSON.parse(String(e.data)) as HubServerFrame;
      } catch {
        return;
      }
      if (frame && typeof frame === 'object' && frame.t !== 'pong') onFrame(frame);
    };
    ws.onclose = (e) => {
      if (ping) clearInterval(ping);
      ping = null;
      if (socket === ws) socket = null;
      if (stopped || isFinal(e.code)) return;
      retry = setTimeout(connect, backoffMs(tries++));
    };
  };

  tellState = state;
  document.addEventListener('visibilitychange', state);
  window.addEventListener('focus', state);
  window.addEventListener('blur', state);
  for (const type of INPUT) window.addEventListener(type, input, { capture: true, passive: true });
  connect();

  return () => {
    stopped = true;
    tellState = () => {};
    if (retry) clearTimeout(retry);
    if (ping) clearInterval(ping);
    document.removeEventListener('visibilitychange', state);
    window.removeEventListener('focus', state);
    window.removeEventListener('blur', state);
    for (const type of INPUT) window.removeEventListener(type, input, { capture: true });
    socket?.close(1000, 'done');
    socket = null;
  };
}
